import React from 'react';
import { useTranslation } from 'react-i18next';

export function BienestarProfessionalBlock({ variant }) {
  const { t } = useTranslation();
  const subKey = {
    yoga: 'bienestarLandings.professionalYogaSub',
    training: 'bienestarLandings.professionalTrainingSub',
    conecta: 'bienestarLandings.professionalConectaSub',
  }[variant];
  const bodyKey = {
    yoga: 'bienestarLandings.professionalYogaBody',
    training: 'bienestarLandings.professionalTrainingBody',
    conecta: 'bienestarLandings.professionalConectaBody',
  }[variant];
  const icon = { yoga: 'spa', training: 'dumbbell', conecta: 'user-friends' }[variant];

  return (
    <section className="bw-section bw-professional" aria-labelledby={`pro-${variant}`}>
      <header className="bw-section-header">
        <h2 id={`pro-${variant}`} className="bw-section-title">{t('bienestarLandings.professionalTitle')}</h2>
        <p className="bw-section-sub">{t(subKey)}</p>
      </header>
      <article className="bw-professional-card">
        <div className="bw-professional-icon" aria-hidden="true">
          <i className={`fas fa-${icon}`} />
        </div>
        <p>{t(bodyKey)}</p>
      </article>
    </section>
  );
}

export function BienestarStoreCta({ variant }) {
  const { t } = useTranslation();
  const noteKey = {
    yoga: 'bienestarLandings.storeYogaNote',
    training: 'bienestarLandings.storeTrainingNote',
    conecta: 'bienestarLandings.storeConectaNote',
  }[variant] || 'bienestarHub.introNote';

  return (
    <aside className="bw-store-note">
      <p>{t(noteKey)}</p>
      <button
        type="button"
        className="bw-link-btn"
        onClick={() => {
          window.history.pushState({}, '', '/tienda');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}
      >
        {t('bienestarHub.introNoteCta')} <i className="fas fa-arrow-right" aria-hidden="true" />
      </button>
    </aside>
  );
}

export function BienestarCrossPaths({ current }) {
  const { t } = useTranslation();
  const paths = [
    { id: 'yoga', path: '/bienestar/yoga', labelKey: 'pathYogaTitle' },
    { id: 'training', path: '/bienestar/entrenamiento', labelKey: 'pathTrainingTitle' },
    { id: 'conecta', path: '/bienestar/conecta', labelKey: 'pathConectaTitle' },
  ].filter((p) => p.id !== current);

  return (
    <nav className="bw-cross-paths" aria-label={t('bienestarLandings.crossPathsLabel')}>
      <p className="bw-cross-paths-lead">{t('bienestarLandings.crossPathsLead')}</p>
      <div className="bw-cross-paths-links">
        {paths.map((p) => (
          <button
            key={p.id}
            type="button"
            className="bw-cross-paths-link"
            onClick={() => {
              window.history.pushState({}, '', p.path);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          >
            {t(`bienestarHub.${p.labelKey}`)}
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        ))}
      </div>
    </nav>
  );
}

export function BienestarLandingHero({ kickerKey, titleKey, leadKey, ctaKey, onCta, imageSrc, fallbackSrc }) {
  const { t } = useTranslation();

  return (
    <section className="bw-landing-hero">
      <div className="bw-landing-hero-bg" aria-hidden="true">
        <img
          src={imageSrc}
          alt=""
          onError={(e) => {
            if (fallbackSrc && !e.currentTarget.src.includes(fallbackSrc.split('/').pop())) {
              e.currentTarget.src = fallbackSrc;
            }
          }}
        />
      </div>
      <div className="bw-landing-hero-overlay" aria-hidden="true" />
      <div className="container bw-container bw-landing-hero-inner">
        <span className="bw-landing-kicker">{t(kickerKey)}</span>
        <h1 className="bw-landing-title">{t(titleKey)}</h1>
        <p className="bw-landing-lead">{t(leadKey)}</p>
        <button type="button" className="bw-cta bw-cta--hero" onClick={onCta}>
          {t(ctaKey)}
        </button>
      </div>
    </section>
  );
}

export function useBienestarPageMeta(titleKey, descKey) {
  const { t, i18n } = useTranslation();
  React.useEffect(() => {
    const prev = document.title;
    document.title = t(titleKey);
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content') || '';
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', t(descKey));
    return () => {
      document.title = prev;
      meta.setAttribute('content', prevDesc);
    };
  }, [t, i18n.language, titleKey, descKey]);
}
